/**
 * Utility functions for managing schedule filters with localStorage persistence
 */

export interface SavedFilter {
  id: string;
  name: string;
  color: string;
  employeeIds: number[];
  createdAt: Date;
  updatedAt: Date;
}

export interface FilterData {
  id: string;
  name: string;
  color: string;
  employeeIds: number[];
  createdAt: string; // ISO string for localStorage
  updatedAt: string; // ISO string for localStorage
}

// Predefined color options for filters
export const FILTER_COLORS = [
  { name: 'Blue', value: '#3B82F6', light: '#DBEAFE' },
  { name: 'Green', value: '#10B981', light: '#D1FAE5' },
  { name: 'Purple', value: '#8B5CF6', light: '#EDE9FE' },
  { name: 'Red', value: '#EF4444', light: '#FEE2E2' },
  { name: 'Orange', value: '#F59E0B', light: '#FEF3C7' },
  { name: 'Pink', value: '#EC4899', light: '#FCE7F3' },
  { name: 'Indigo', value: '#6366F1', light: '#E0E7FF' },
  { name: 'Teal', value: '#14B8A6', light: '#CCFBF1' },
];

const STORAGE_KEY = 'schedule-filters';

/**
 * Get all saved filters from localStorage
 */
export function getSavedFilters(): SavedFilter[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const data: FilterData[] = JSON.parse(stored);
    return data.map(item => ({
      ...item,
      createdAt: new Date(item.createdAt),
      updatedAt: new Date(item.updatedAt),
    }));
  } catch (error) {
    console.warn('Failed to load saved filters:', error);
    return [];
  }
}

/**
 * Save a filter to localStorage
 */
export function saveFilter(filter: Omit<SavedFilter, 'id' | 'createdAt' | 'updatedAt'>): SavedFilter {
  const filters = getSavedFilters();
  const now = new Date();
  
  const newFilter: SavedFilter = {
    ...filter,
    id: generateFilterId(),
    createdAt: now,
    updatedAt: now,
  };
  
  const updatedFilters = [...filters, newFilter];
  saveFiltersToStorage(updatedFilters);
  
  return newFilter;
}

/**
 * Update an existing filter
 */
export function updateFilter(id: string, updates: Partial<Pick<SavedFilter, 'name' | 'color' | 'employeeIds'>>): SavedFilter | null {
  const filters = getSavedFilters();
  const filterIndex = filters.findIndex(f => f.id === id);
  
  if (filterIndex === -1) return null;
  
  const updatedFilter: SavedFilter = {
    ...filters[filterIndex],
    ...updates,
    updatedAt: new Date(),
  };
  
  filters[filterIndex] = updatedFilter;
  saveFiltersToStorage(filters);
  
  return updatedFilter;
}

/**
 * Delete a filter
 */
export function deleteFilter(id: string): boolean {
  const filters = getSavedFilters();
  const filteredFilters = filters.filter(f => f.id !== id);
  
  if (filteredFilters.length === filters.length) return false;
  
  saveFiltersToStorage(filteredFilters);
  return true;
}

/**
 * Get a specific filter by ID
 */
export function getFilterById(id: string): SavedFilter | null {
  const filters = getSavedFilters();
  return filters.find(f => f.id === id) || null;
}

/**
 * Check if a filter name already exists
 */
export function isFilterNameTaken(name: string, excludeId?: string): boolean {
  const filters = getSavedFilters();
  return filters.some(f => f.name.toLowerCase() === name.toLowerCase() && f.id !== excludeId);
}

/**
 * Generate a unique filter ID
 */
function generateFilterId(): string {
  return `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Save filters array to localStorage
 */
function saveFiltersToStorage(filters: SavedFilter[]): void {
  try {
    const data: FilterData[] = filters.map(filter => ({
      ...filter,
      createdAt: filter.createdAt.toISOString(),
      updatedAt: filter.updatedAt.toISOString(),
    }));
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save filters to localStorage:', error);
  }
}

/**
 * Apply a filter to employee data
 */
export function applyEmployeeFilter(employees: any[], filter: SavedFilter | null): any[] {
  if (!filter || filter.employeeIds.length === 0) {
    return employees;
  }
  
  return employees.filter(employee => filter.employeeIds.includes(employee.id));
}

/**
 * Get filter color by value
 */
export function getFilterColor(colorValue: string) {
  return FILTER_COLORS.find(c => c.value === colorValue) || FILTER_COLORS[0];
}
